/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useMemo} from 'react';
import {useQuery, useQueryClient} from 'react-query';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {getCurrentFileHistory} from '@ion/core/src/services/Connect';
import {GenericObject} from '@ion/core/src/types/entities';
import {createKey, QueryKey} from '../config/QueryKeys';
import {createQueryResult, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseCurrentFileHistoryResult = UseDefaultQueryResult & {
  fileHistory: GenericObject[];
  refresh: () => void;
};

/**
 * Retrieves the device current file history for the given device esn.
 *
 * ```typescript
 *
 * import {useCurrentFileHistory} from '@ion/query/src/hooks/';
 *
 *  ...
 *
 *  const {fileHistory} = useCurrentFileHistory();
 *
 * ```
 *
 * import {useCurrentFileHistory} from '@ion/query/src/hooks/';
 * @param esn of the device to retrieve file history for
 * @param staleTime how long the query should hold onto details before re-fetching
 * @returns useCurrentFileHistory containing the query results and the file history.
 */
export const useCurrentFileHistory = (
  esn: string,
  staleTime = getStaleTime(1, false, false)
): UseCurrentFileHistoryResult => {
  const queryClient = useQueryClient();
  const key         = useMemo(() => (
    createKey(QueryKey.CURRENT_FILE_HISTORY, esn)
  ), [esn]);
  const query       = useQuery(key, async() => {
    const results = await getCurrentFileHistory(esn);
    return results;
  }, {
    staleTime : staleTime as number,
    enabled   : !!esn
  });

  useEffect(() => {
    if (!query.data && esn && !query.isError) {
      query.refetch();
    }
  }, [query, esn]);

  const refresh = useCallback(() => {
    queryClient.invalidateQueries(key);
  }, [key]);

  return createQueryResult(query, {
    fileHistory : query.data || {},
    refresh
  }) as UseCurrentFileHistoryResult;
};

export default useCurrentFileHistory;
